import { client } from '@admin/libraries/apollo';
import { PollForInvoiceEventDocument } from '@admin/schema';

interface IQueryData {
  billing: {
    invoiceEvent: {
      id: string;
      completed: boolean;
    } | null;
  } | null;
}

interface IQueryVariables {
  accountID: string;
  attemptID: string;
}

const sleep = async (interval: number) => new Promise((resolve) => setTimeout(resolve, interval));

/** ~72s total at default interval; aligns with invoice pay modal polling duration. */
const DEFAULT_MAX_ATTEMPTS = 24;

/**
 * Polls until the invoice event completes or until attempts are exhausted.
 *
 * @throws Error when polling times out or the query returns an unexpected shape.
 */
export const pollForInvoiceEvent = async (
  accountID: string,
  attemptID: string,
  interval = 3000,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
) => {
  let attempts = 0;
  while (attempts < maxAttempts) {
    attempts += 1;
    const { data } = await client.query<IQueryData, IQueryVariables>({
      fetchPolicy: 'no-cache',
      query: PollForInvoiceEventDocument,
      variables: {
        accountID,
        attemptID,
      },
    });
    const completed = data?.billing?.invoiceEvent?.completed === true;
    if (completed) {
      return;
    }

    await sleep(interval);
  }

  throw new Error('Timed out waiting for the invoice action to finish. Please refresh the page.');
};
